import { useState } from 'react';

interface ConfirmOptions {
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  /** Только кнопка подтверждения, без "Отмена" — для информационных сообщений (напр. ошибка сохранения), не для решений. */
  hideCancel?: boolean;
}

interface ConfirmRequest {
  message: string;
  options: ConfirmOptions;
  resolve: (result: boolean) => void;
}

/**
 * Замена window.confirm() — свой диалог по центру экрана в стиле страниц
 * (см. .modal/.modal-overlay в main.scss), а не системное окно браузера.
 * confirm() возвращает Promise<boolean>, использование то же самое, что и
 * у window.confirm — await confirm('...'). Общий хук для страницы списка
 * клиентов и карточки клиента — то же самое поведение подтверждения удаления
 * должно быть в обоих местах.
 */
export function useConfirmDialog() {
  const [request, setRequest] = useState<ConfirmRequest | null>(null);

  const confirm = (message: string, options: ConfirmOptions = {}) =>
    new Promise<boolean>((resolve) => setRequest({ message, options, resolve }));

  const close = (result: boolean) => {
    request?.resolve(result);
    setRequest(null);
  };

  /** Информационное сообщение (напр. ошибка сохранения) — один "Понятно" вместо выбора да/нет. */
  const alertDialog = (message: string, confirmLabel = 'Понятно') =>
    confirm(message, { confirmLabel, hideCancel: true });

  const dialog = request && (
    <div className="modal-overlay" onClick={() => close(false)}>
      <div className="modal" role="alertdialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <p className="modal__message">{request.message}</p>
        <div className="modal__actions">
          {!request.options.hideCancel && (
            <button type="button" className="btn btn--secondary" onClick={() => close(false)}>
              {request.options.cancelLabel ?? 'Отмена'}
            </button>
          )}
          <button
            type="button"
            className={`btn ${request.options.danger ? 'btn--danger' : 'btn--primary'}`}
            onClick={() => close(true)}
          >
            {request.options.confirmLabel ?? 'Подтвердить'}
          </button>
        </div>
      </div>
    </div>
  );

  return { confirm, alert: alertDialog, dialog };
}
